import { createFileRoute } from '@tanstack/react-router'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DataTable, Panel, StatTile } from '#/components/admin/ui'
import { useAdminMarketing, type AdminMarketing } from '#/lib/api-admin'
import { fmtNumber } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/marketing')({ component: adminPage(Page) })
type Src = AdminMarketing['signupsBySource'][number]
type Attr = AdminMarketing['attribution'][number]

function Page() {
  const { data, isLoading, isError, isFetching, refetch } = useAdminMarketing()
  return (
    <AdminShell title={m.admin_nav_marketing()} description={m.admin_marketing_desc()}>
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label={m.admin_mkt_events()} value={data ? fmtNumber(data.totals.events) : '…'} />
        <StatTile label={m.admin_mkt_attributed_users()} value={data ? fmtNumber(data.totals.users) : '…'} />
        <StatTile label={m.admin_mkt_sources()} value={data ? fmtNumber(data.signupsBySource.length) : '…'} />
      </div>
      <Panel title={m.admin_mkt_signups_by_source()}>
        <DataTable<Src>
          rows={data?.signupsBySource}
          loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
          rowKey={(r) => r.source ?? '(none)'}
          cols={[
            { key: 'src', header: m.admin_col_source(), render: (r) => r.source ?? <span className="text-muted-foreground">{m.admin_mkt_unknown()}</span> },
            { key: 'n', header: m.admin_col_signups(), render: (r) => fmtNumber(r.count), className: 'text-right tabular-nums' },
            { key: 'd30', header: m.admin_col_last30(), render: (r) => fmtNumber(r.new30d), className: 'text-right tabular-nums' },
          ]}
        />
      </Panel>
      <Panel title={m.admin_mkt_attribution()}>
        <DataTable<Attr>
          rows={data?.attribution}
          loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
          rowKey={(r) => `${r.source}|${r.medium}|${r.campaign}`}
          cols={[
            { key: 'src', header: m.admin_col_source(), render: (r) => r.source ?? '—' },
            { key: 'med', header: m.admin_col_medium(), render: (r) => r.medium ?? '—' },
            { key: 'camp', header: m.admin_col_campaign(), render: (r) => r.campaign ?? '—' },
            { key: 'n', header: m.admin_col_events(), render: (r) => fmtNumber(r.count), className: 'text-right tabular-nums' },
            { key: 'u', header: m.admin_col_users(), render: (r) => fmtNumber(r.users), className: 'text-right tabular-nums' },
          ]}
        />
      </Panel>
      <Panel title={m.admin_mkt_landing()}>
        <DataTable<AdminMarketing['landingPaths'][number]>
          rows={data?.landingPaths}
          loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
          rowKey={(r) => r.landingPath ?? '(none)'}
          cols={[
            { key: 'p', header: m.admin_col_landing(), render: (r) => <span className="font-mono text-xs">{r.landingPath ?? '—'}</span> },
            { key: 'n', header: m.admin_col_events(), render: (r) => fmtNumber(r.count), className: 'text-right tabular-nums' },
          ]}
        />
      </Panel>
    </AdminShell>
  )
}
